import { Circle } from 'lucide-react';
import { useState } from 'react';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { isRecordingActive } from '../realtime/frames';
import type { RecordingState } from '../realtime/frames';

/** The recording snapshot the top bar renders, shaped like the realtime hook's field. */
export interface RecordingControlsSnapshot {
  readonly state: RecordingState;
  readonly recordingId: number;
}

export interface RecordingControlsProps {
  /** Latest room recording state; null when nothing is known. */
  readonly recording: RecordingControlsSnapshot | null;
  /** True for moderator/admin: the toggle button renders at all. */
  readonly canControl: boolean;
  /** True while a start/stop request is in flight (the toggle disables). */
  readonly busy: boolean;
  readonly onStart: () => void;
  readonly onStop: () => void;
}

/**
 * The top-bar recording surfaces (M6): a red REC indicator while the room records, and - for
 * moderators/admins only - the recording toggle. Starting is immediate; stopping asks a short
 * inline confirmation first (rec-stop-confirm) so an accidental click cannot end the room's
 * recording. The server remains authoritative: the WS recording frames flip the indicator.
 */
export function RecordingControls(props: RecordingControlsProps): JSX.Element | null {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const active = props.recording !== null && isRecordingActive(props.recording.state);
  // A request in flight or a stop the server is already finalizing: further toggles wait.
  const transitioning = props.busy || props.recording?.state === 'stopping';

  return (
    <div className="flex shrink-0 items-center gap-2">
      {props.recording !== null && active ? (
        <span
          data-testid="rec-indicator"
          title={t(`recording.state_${props.recording.state}`)}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-red-500/10 px-2 py-1 text-[11px] font-semibold tracking-wider text-red-400 uppercase"
        >
          <span
            aria-hidden
            className={cn(
              'size-1.5 rounded-full bg-red-500',
              props.recording.state !== 'stopping' && 'animate-pulse',
            )}
          />
          REC · {t(`recording.state_${props.recording.state}`)}
        </span>
      ) : null}
      {props.canControl ? (
        confirming ? (
          <span data-testid="rec-stop-confirm" className="flex shrink-0 items-center gap-1.5">
            <span className="text-[11px] font-medium text-red-200">
              {t('recording.stop_confirm')}
            </span>
            <button
              type="button"
              data-testid="rec-stop-yes"
              aria-label={t('recording.stop_confirm_yes_aria')}
              disabled={props.busy}
              onClick={() => {
                setConfirming(false);
                props.onStop();
              }}
              className="rounded-md bg-red-500/90 px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t('recording.stop')}
            </button>
            <button
              type="button"
              data-testid="rec-stop-no"
              aria-label={t('recording.stop_confirm_no_aria')}
              onClick={() => {
                setConfirming(false);
              }}
              className="rounded-md border border-white/10 px-2 py-1 text-[11px] font-medium text-zinc-300 transition-colors hover:bg-white/10"
            >
              {t('common.cancel')}
            </button>
          </span>
        ) : (
          <button
            type="button"
            data-testid="rec-toggle"
            aria-label={active ? t('recording.stop') : t('recording.start')}
            title={active ? t('recording.stop') : t('recording.start')}
            disabled={transitioning}
            onClick={() => {
              if (active) {
                setConfirming(true);
                return;
              }
              props.onStart();
            }}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40',
              active
                ? 'bg-red-500/10 text-red-300 hover:bg-red-500/20'
                : 'bg-white/[0.05] text-zinc-300 hover:bg-white/10 hover:text-white',
            )}
          >
            <Circle
              aria-hidden
              className={cn('h-3 w-3', active ? 'fill-red-400 text-red-400' : 'text-red-400')}
            />
            {active ? t('recording.stop') : t('recording.start')}
          </button>
        )
      ) : null}
    </div>
  );
}
